import { ENV } from '../../config/runtimeEnv';
import {
	Trait,
	TraitSummary
} from '../../types/geneCodes/TraitTypes';
import { Staging } from '../../types/geneCodes/StagingTypes';
import { http } from '../../lib/http/client';

const API_BASE_URL = ENV.VITE_LABIQ_SERVER || '';

// Helper for common API headers

/**
 * Fetch all traits with pagination and filters
 */
export const fetchTraits = async (
	page: number = 1,
	perPage: number = 25,
	sortBy: string = 'trait_name',
	sortOrder: string = 'asc',
	filters: {
		trait_panel?: string;
		search?: string;
	} = {}
): Promise<{
	data: Trait[];
	total: number;
}> => {

	const cleanedFilters = Object.fromEntries(
		Object.entries(filters).filter(([_, value]) => value !== "")
	);

	const response = await http.get(`${API_BASE_URL}/traits`, {
		params: {
			page,
			per_page: perPage,
			sort_by: sortBy,
			sort_order: sortOrder,
			...cleanedFilters
		}
	});

	return {
		data: response.data.data,
		total: response.data.total
	}
};

/**
 * Fetch a single trait by ID
 */
export const fetchTrait = async (id: number): Promise<Trait> => {
	const response = await http.get(`${API_BASE_URL}/traits/${id}`);

	return response.data.data;
};

/**
 * Create a new trait
 */
export const createTrait = async (trait: Partial<Trait>): Promise<Trait> => {
	const response = await http.post(
		`${API_BASE_URL}/traits`,
		trait
	);

	return response.data.data;
};

/**
 * Update an existing trait
 */
export const updateTrait = async (id: number, trait: Partial<Trait>): Promise<Trait> => {
	const response = await http.put(
		`${API_BASE_URL}/traits/${id}`,
		trait
	);

	return response.data.data;
};

/**
 * Delete a trait
 */
export const deleteTrait = async (id: number): Promise<void> => {
	await http.delete(`${API_BASE_URL}/traits/${id}`);
};

/**
 * Get all SNPs for a trait
 */
export const getTraitSnps = async (
	id: number,
	page: number = 1,
	perPage: number = 25,
	sortBy: string = 'rs',
	sortOrder: string = 'asc',
	filters: {
		gene?: string;
		quality?: number;
		search?: string;
	} = {}
): Promise<{Data: Staging[];
	Trait: Trait;
}> => {
	const response = await http.get(`${API_BASE_URL}/traits/${id}/snps`, {
		params: {
			page,
			per_page: perPage,
			sort_by: sortBy,
			sort_order: sortOrder,
			...filters
		}
	});

	return {
		Data: response.data.data,
		Trait: response.data.trait
	}
};

/**
 * Add SNPs to a trait
 */
export const addSnpsToTrait = async (
	id: number,
	snpIds: number[]
): Promise<{ message: string; updated_count: number }> => {
	const response = await http.post(
		`${API_BASE_URL}/traits/${id}/add-snps`,
		{ snp_ids: snpIds }
	);

	return response.data;
};

/**
 * Remove SNPs from a trait
 */
export const removeSnpsFromTrait = async (
	id: number,
	snpIds: number[]
): Promise<{ message: string; updated_count: number }> => {
	const response = await http.post(
		`${API_BASE_URL}/traits/${id}/remove-snps`,
		{ snp_ids: snpIds }
	);

	return response.data;
};

/**
 * Get a summary of trait data
 */
export const getTraitSummary = async (id: number): Promise<TraitSummary> => {
	const response = await http.get(`${API_BASE_URL}/traits/${id}/summary`);

	return response.data.data;
};

/**
 * Get all traits for a specific panel
 */
export const getTraitsByPanel = async (
	panelName: string,
	search?: string,
	sortBy: string = 'trait_name',
	sortOrder: string = 'asc'
): Promise<Trait[]> => {
	const response = await http.get(`${API_BASE_URL}/traits/panel/${panelName}`, {
		params: {
			search,
			sort_by: sortBy,
			sort_order: sortOrder
		}
	});

	return response.data.data;
};

/**
 * Search for traits
 */
export const searchTraits = async (
	search: string,
	page: number = 1,
	perPage: number = 25,
	sortBy: string = 'trait_name',
	sortOrder: string = 'asc',
	traitPanel?: string
): Promise<{
	data: Trait[];
	total: number;
}> => {
	const response = await http.post(`${API_BASE_URL}/traits/search`,
		{ search },
		{
			params: {
				page,
				per_page: perPage,
				sort_by: sortBy,
				sort_order: sortOrder,
				trait_panel: traitPanel
			}
		}
	);

	return {
		data: response.data.data,
		total: response.data.total
	};
};